import React, { useState } from 'react';
import apiClient from '../services/unifiedApiClient';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

/**
 * Modal for linking a Google account to an existing local account.
 * Shown when a user tries to log in with Google but already has
 * a local account with the same email.
 */
const LinkAccountModal = ({
  isOpen,
  onClose,
  onSuccess,
  accountInfo // { email, google_id, google_name, google_picture }
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen || !accountInfo) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const googleToken = sessionStorage.getItem('google_access_token');
      if (!googleToken) {
        setError('Google authentication expired. Please try again.');
        setLoading(false);
        return;
      }

      const response = await apiClient.linkGoogleAccount(googleToken, password);
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        apiClient.setAuthToken(response.access_token);
        onSuccess(response);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Incorrect password. Please try again.');
      } else {
        setError(err.userMessage || 'Failed to link accounts. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword('');
    setError(null);
    sessionStorage.removeItem('google_access_token');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(46,33,9,0.35)',
    }}>
      <div style={{
        backgroundColor: T.card,
        border: `1px solid ${T.rule}`,
        borderRadius: T.r4,
        width: '100%',
        maxWidth: '440px',
        margin: '0 16px',
        boxShadow: '0 8px 32px rgba(46,33,9,0.22), 0 2px 0 rgba(46,33,9,0.04)',
        fontFamily: T.sans,
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '28px' }}>

            {/* Header */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: T.ink,
                margin: 0,
                fontFamily: T.sans,
              }}>
                Link Your Accounts
              </h3>
            </div>

            {/* Google account info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: T.paperSoft,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r3,
              padding: '12px',
              marginBottom: '16px',
            }}>
              {accountInfo.google_picture ? (
                <img
                  src={accountInfo.google_picture}
                  alt={accountInfo.google_name || 'Profile'}
                  referrerPolicy="no-referrer"
                  style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: T.orange,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '16px',
                  flexShrink: 0,
                  fontFamily: T.sans,
                }}>
                  {accountInfo.google_name?.[0] || accountInfo.email?.[0] || 'G'}
                </div>
              )}
              <div style={{ marginLeft: '12px' }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: T.ink,
                  fontFamily: T.sans,
                }}>
                  {accountInfo.google_name || 'Google Account'}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: T.inkMuted,
                  fontFamily: T.sans,
                }}>
                  {accountInfo.email}
                </p>
              </div>
            </div>

            {/* Message */}
            <p style={{
              fontSize: '14px',
              color: T.inkMuted,
              marginBottom: '16px',
              lineHeight: 1.55,
              fontFamily: T.sans,
            }}>
              An account with this email already exists. Enter your password to link your Google account for easier sign-in.
            </p>

            {/* Password input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: T.ink2,
                marginBottom: '6px',
                fontFamily: T.sans,
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your account password"
                required
                autoFocus
                disabled={loading}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 12px',
                  backgroundColor: T.paperSoft,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.ink,
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: T.sans,
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = T.orange; }}
                onBlur={(e) => { e.target.style.borderColor = T.rule; }}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: `${T.capRed}12`,
                border: `1px solid ${T.capRed}44`,
                borderRadius: T.r2,
                marginBottom: '16px',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: T.capRed,
                  fontFamily: T.sans,
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                style={{
                  padding: '8px 18px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: T.inkMuted,
                  backgroundColor: 'transparent',
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  cursor: 'pointer',
                  fontFamily: T.sans,
                  transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = T.ruleStrong;
                  e.target.style.color = T.ink2;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = T.rule;
                  e.target.style.color = T.inkMuted;
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !password}
                style={{
                  padding: '8px 18px',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#fff',
                  backgroundColor: loading || !password ? `${T.orange}88` : T.orange,
                  border: 'none',
                  borderRadius: T.r2,
                  cursor: loading || !password ? 'not-allowed' : 'pointer',
                  opacity: loading || !password ? 0.7 : 1,
                  fontFamily: T.sans,
                  transition: 'opacity 0.15s, background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!loading && password) e.target.style.backgroundColor = T.orangeDark;
                }}
                onMouseLeave={(e) => {
                  if (!loading && password) e.target.style.backgroundColor = T.orange;
                }}
              >
                {loading ? 'Linking...' : 'Link Accounts'}
              </button>
            </div>

            {/* Alternative */}
            <div style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: `1px dashed ${T.rule}`,
              textAlign: 'center',
            }}>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: T.inkFaint,
                fontFamily: T.sans,
              }}>
                Or{' '}
                <a href="/login" style={{ color: T.orange, textDecoration: 'none', fontWeight: 600 }}>
                  sign in with email and password
                </a>
              </p>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkAccountModal;
